import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Whole rows, with top three retained; every row is reachable within one slide slot. */
export function useSlideRows<T>(rows: readonly T[], capacity: number, duration: number, active: boolean, pinned = 3): T[] {
  const [page, setPage] = useState(0);
  const pages = rows.length <= capacity ? 1 : Math.ceil((rows.length - pinned) / (capacity - pinned));
  useEffect(() => {
    setPage(0);
    if (!active || pages <= 1) return;
    const timer = window.setInterval(() => setPage(n => (n + 1) % pages), Math.max(800, Math.floor(duration / pages)));
    return () => window.clearInterval(timer);
  }, [active, pages, duration]);
  if (pages === 1) return [...rows];
  const offset = (page % pages) * (capacity - pinned);
  return [...rows.slice(0, pinned), ...rows.slice(pinned + offset, pinned + offset + capacity - pinned)];
}

/** Keep previous visible positions across slide visits; paging is never a rank animation. */
export function useFlipRows(revision: number, active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const previous = useRef(new Map<string, number>());
  const seenRevision = useRef(0);
  useLayoutEffect(() => {
    const container = ref.current;
    if (!active || !container) return;
    const nodes = [...container.querySelectorAll<HTMLElement>('[data-row-id]')];
    const scale = container.offsetWidth ? container.getBoundingClientRect().width / container.offsetWidth : 1;
    const positions = new Map(nodes.map(node => [node.dataset.rowId!, node.getBoundingClientRect().top / (scale || 1)]));
    if (revision > seenRevision.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (const node of nodes) {
        const tint = ({ up: 'rgba(31,157,85,.38)', down: 'rgba(224,27,34,.30)', new: 'rgba(217,164,65,.40)' })[node.dataset.move ?? ''];
        if (tint) node.animate?.([{ backgroundColor: tint }, { backgroundColor: tint, offset: .4 }, { backgroundColor: getComputedStyle(node).backgroundColor }], { duration: 1800 });
        const old = previous.current.get(node.dataset.rowId!);
        const y = positions.get(node.dataset.rowId!)!;
        if (old != null && old !== y) node.animate?.([{ transform: `translateY(${old - y}px)` }, { transform: 'translateY(0)' }], { duration: 800, easing: 'cubic-bezier(.2,.8,.2,1)' });
        else if (old == null && node.dataset.move === 'new') node.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 800 });
      }
    }
    previous.current = positions;
    seenRevision.current = revision;
  });
  return ref;
}
